// =======================================================
// PrivacyPolicyEditor — Privacy Policy Manager
// File: src/views/pages/booking-engine/admin/PrivacyPolicyEditor.jsx
// Toggle privacy policy visibility and manage rich-text
// content via ReactQuill. Saves under branding.privacyPolicy.
// =======================================================

import React, { useState, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Form,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Alert,
} from "react-bootstrap";

// =============================
// DEFAULT PRIVACY POLICY
// =============================

const DEFAULT_PRIVACY_HTML = `
<h3>Privacy Policy</h3>

<p>This Privacy Policy describes how we collect, use, and protect your personal information when you use our booking engine and related services.</p>

<h4>1. Information We Collect</h4>
<ul>
  <li><strong>Personal details:</strong> Name, email address, phone number, and billing address provided during booking.</li>
  <li><strong>Payment information:</strong> Credit/debit card details processed securely through our payment gateway.</li>
  <li><strong>Stay details:</strong> Check-in/check-out dates, room preferences, and special requests.</li>
  <li><strong>Device & usage data:</strong> IP address, browser type, and pages visited for analytics purposes.</li>
</ul>

<h4>2. How We Use Your Information</h4>
<ul>
  <li>To process and confirm your reservations.</li>
  <li>To communicate booking confirmations, updates, and support.</li>
  <li>To improve our services and personalise your experience.</li>
  <li>To comply with legal and regulatory requirements.</li>
</ul>

<h4>3. Data Sharing</h4>
<ul>
  <li>We do not sell your personal information to third parties.</li>
  <li>Data may be shared with payment processors and service providers strictly for completing your booking.</li>
  <li>We may disclose information if required by law or to protect our rights.</li>
</ul>

<h4>4. Data Security</h4>
<p>We implement industry-standard security measures including SSL encryption, access controls, and secure data storage to protect your information.</p>

<h4>5. Your Rights</h4>
<ul>
  <li>You may request access to, correction of, or deletion of your personal data.</li>
  <li>You may opt out of marketing communications at any time.</li>
</ul>

<h4>6. Contact Us</h4>
<p>For privacy-related inquiries, please contact the property directly using the information provided in the Contact &amp; Location section.</p>
`.trim();

// =============================
// QUILL EDITOR CONFIG
// =============================

const editorModules = {
  toolbar: {
    container: [
      [{ header: [2, 3, 4, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  },
};

// =============================
// COMPONENT
// =============================

export default function PrivacyPolicyEditor({ branding, setBranding }) {
  const [showHtmlSource, setShowHtmlSource] = useState(false);

  // ------- section helpers -------
  const createDefaultSection = () => ({
    enabled: false,
    title: "Privacy Policy",
    content: "",
    lastUpdated: null,
  });

  const section = useMemo(() => {
    const raw = branding?.privacyPolicy;
    if (raw && typeof raw === "object") return raw;
    return createDefaultSection();
  }, [branding?.privacyPolicy]);

  const updateSection = (updater) => {
    setBranding((prev) => {
      const base = prev?.privacyPolicy || createDefaultSection();
      const updated = updater(base);
      return { ...prev, privacyPolicy: updated };
    });
  };

  const patchField = (field, value) => {
    updateSection((sec) => ({ ...sec, [field]: value }));
  };

  // ------- derived -------
  const contentLength = (section.content || "")
    .replace(/<[^>]*>/g, "")
    .trim().length;

  const hasContent = contentLength > 0;

  // ------- handlers -------
  const handleToggle = (checked) => {
    patchField("enabled", checked);
  };

  const handleContentChange = (html) => {
    updateSection((sec) => ({
      ...sec,
      content: html,
      lastUpdated: new Date().toISOString(),
    }));
  };

  const handleLoadDefault = () => {
    if (
      hasContent &&
      !window.confirm(
        "This will replace the current privacy policy with the default template. Continue?"
      )
    )
      return;
    updateSection((sec) => ({
      ...sec,
      content: DEFAULT_PRIVACY_HTML,
      lastUpdated: new Date().toISOString(),
    }));
  };

  const handleClearContent = () => {
    if (!window.confirm("Clear the entire privacy policy content?")) return;
    updateSection((sec) => ({
      ...sec,
      content: "",
      lastUpdated: new Date().toISOString(),
    }));
  };

  // =============================
  // RENDER
  // =============================
  return (
    <div className="pt-3">
      {/* Section header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div className="d-flex flex-column flex-sm-row gap-2 align-items-start align-items-sm-center">
          <Form.Label className="mb-0 fw-semibold">Privacy Policy</Form.Label>
          <Badge bg="light" text="dark">
            Displayed at checkout &amp; footer
          </Badge>
        </div>
      </div>

      {/* Main toggle */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-semibold mb-1">Show Privacy Policy</div>
              <small className="text-muted">
                When enabled, the privacy policy will be visible on your booking
                engine (checkout page &amp; footer link).
              </small>
            </div>
            <Form.Check
              type="switch"
              id="privacy-policy-enabled"
              checked={!!section.enabled}
              onChange={(e) => handleToggle(e.target.checked)}
              label={section.enabled ? "Visible" : "Hidden"}
              className="fs-5"
            />
          </div>
        </Card.Body>
      </Card>

      {/* Content area — only when enabled */}
      {!section.enabled ? (
        <Alert variant="light" className="border">
          <div className="text-muted">
            Turn the toggle <strong>On</strong> above to write and display a privacy policy on your booking engine.
          </div>
        </Alert>
      ) : (
        <>
          {/* Title */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Page Title</Form.Label>
                <Form.Control
                  type="text"
                  value={section.title || "Privacy Policy"}
                  onChange={(e) => patchField("title", e.target.value)}
                  placeholder="e.g. Privacy Policy"
                />
                <small className="text-muted">
                  Heading shown at the top of the privacy policy page.
                </small>
              </Form.Group>
            </Col>
            <Col
              md={6}
              className="d-flex align-items-end justify-content-end gap-2 pb-1"
            >
              <Button
                size="sm"
                variant="outline-primary"
                onClick={handleLoadDefault}
              >
                Load default template
              </Button>
              {hasContent && (
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={handleClearContent}
                >
                  Clear all
                </Button>
              )}
            </Col>
          </Row>

          {/* Editor */}
          <Card className="border-0 shadow-sm mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="fw-semibold">
                  Policy Content
                  {contentLength > 0 && (
                    <Badge bg="secondary" className="ms-2">
                      {contentLength} chars
                    </Badge>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="light"
                  className="border"
                  onClick={() => setShowHtmlSource(!showHtmlSource)}
                >
                  {showHtmlSource ? "Editor View" : "HTML Source"}
                </Button>
              </div>

              <small className="text-muted d-block mb-2">
                Write your privacy policy using the rich editor below. You can
                use headings, bold, lists, and links.
              </small>

              {showHtmlSource ? (
                <textarea
                  value={section.content || ""}
                  onChange={(e) => handleContentChange(e.target.value)}
                  style={{
                    width: "100%",
                    minHeight: "320px",
                    fontFamily: "monospace",
                    fontSize: "13px",
                    border: "1px solid #ccc",
                    borderRadius: 6,
                    padding: "12px",
                  }}
                />
              ) : (
                <ReactQuill
                  value={section.content || ""}
                  onChange={handleContentChange}
                  modules={editorModules}
                  theme="snow"
                  placeholder="Start writing your privacy policy here..."
                  style={{ minHeight: "250px" }}
                />
              )}
            </Card.Body>
          </Card>

          {/* Live Preview */}
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="fw-semibold mb-2">Live Preview</div>
              <div
                className="p-3 rounded border bg-light"
                style={{ maxHeight: "400px", overflow: "auto" }}
              >
                {hasContent ? (
                  <>
                    <h5 className="fw-bold mb-3">
                      {section.title || "Privacy Policy"}
                    </h5>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: section.content,
                      }}
                    />
                  </>
                ) : (
                  <div className="text-muted text-center py-4">
                    No content yet. Use the editor above or load the default
                    template to get started.
                  </div>
                )}
              </div>

              {section.lastUpdated && (
                <small className="text-muted d-block mt-2">
                  Last updated:{" "}
                  {new Date(section.lastUpdated).toLocaleString()}
                </small>
              )}
            </Card.Body>
          </Card>

          {/* Info alert */}
          <Alert variant="info" className="mt-3 small">
            <strong>How it works:</strong> The privacy policy content is stored
            in the <code>privacyPolicy</code> configuration node and served to
            guests on the booking engine. Changes take effect after you click{" "}
            <strong>Save changes</strong> at the top.
          </Alert>
        </>
      )}
    </div>
  );
}